import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  Slide,
  Fade,
} from "@mui/material";
import {
  primaryButtonStyles,
  tertiaryButtonStyles,
} from "../../../Constants/buttonStyles";

export default function QuestionnaireCarousel({
  questions,
  botData,
  setBotData,
  applyDecisionLogic,
  handleCreateBot,
  organization,
  priorityList,
  setPriorityList,
  domain,
  setDomain,
}) {
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState(questions.map(() => null));
  const [showDomainInput, setShowDomainInput] = useState(false);

  // fade-in control
  const [visible, setVisible] = useState(true);

  const doFade = (newIndex) => {
    setVisible(false);
    setTimeout(() => {
      setCurrent(newIndex);
      setVisible(true);
    }, 500);
  };

  const handleAnswer = (idx, value) => {
    const a = [...answers];
    a[idx] = value;
    setAnswers(a);

    // update botData immediately for Q0 and Q1
    if (idx === 0) {
      setBotData((prev) => ({
        ...prev,
        workflow: {
          ...prev.workflow,
          retrieval_1: {
            ...prev.workflow.retrieval_1,
            metric: {
              ...prev.workflow.retrieval_1.metric,
              hybrid:
                value === "Yes" && organization.subscription !== "starter",
            },
          },
        },
      }));
    }
    if (idx === 1) {
      setBotData((prev) => ({
        ...prev,
        workflow: {
          ...prev.workflow,
          retrieval_1: {
            ...prev.workflow.retrieval_1,
            parser: {
              ...prev.workflow.retrieval_1.parser,
              llama_parse:
                value === "Yes" && organization.subscription !== "starter",
            },
          },
        },
      }));
    }

    // // for the domain question
    // if (idx === 2) {
    //   if (value === "Yes") {
    //     setShowDomainInput(true);
    //     return; // wait for user to type domain and click Next
    //   } else {
    //     // if "No", clear any domain and auto-advance
    //     setShowDomainInput(false);
    //     setDomain("");
    //     setCurrent((c) => Math.min(c + 1, questions.length - 1));
    //     return;
    //   }
    // }

    // auto-advance for Q0, Q1
    // if (idx < 2) {
    //   setCurrent((c) => Math.min(c + 1, questions.length - 1));
    // }
    // set direction & animate
    // fade to next question automatically
    doFade(Math.min(idx + 1, questions.length - 1));
  };

  const next = () => {
    // on the domain question, commit domain to botData
    if (current === 2 && answers[2] === "Yes") {
      if (!domain.trim()) return; // require domain
      setBotData((prev) => ({
        ...prev,
        problem_statement: domain.trim(),
      }));
    }

    if (current === questions.length - 1) {
      handleCreateBot(botData);
    } else {
      doFade(current + 1);
      if (current + 1 !== 2) {
        setShowDomainInput(false);
        setDomain("");
      }
    }
  };

  const prev = () => {
    if (current === 0) return;
    doFade(current - 1);
  };

  // drag & drop handlers (unchanged)
  const onDragStart = (i) => (e) => e.dataTransfer.setData("idx", i);
  const onDrop = (i) => (e) => {
    e.preventDefault();
    const from = +e.dataTransfer.getData("idx");
    const updated = [...priorityList];
    const [item] = updated.splice(from, 1);
    updated.splice(i, 0, item);
    setPriorityList(updated);
    applyDecisionLogic(updated);
  };
  const onDragOver = (e) => e.preventDefault();

  const { question, options } = questions[current];
  const answer = answers[current];

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        p: 3,
        bgcolor: "#1A1A24",
        overflowY: "auto",
        overflowX: "hidden",
        height: "auto",
        borderRadius: 2,
        width: "97%",
      }}
    >
      <Fade in={visible} timeout={500} mountOnEnter unmountOnExit>
        <Box sx={{ overflowX: "hidden" }}>
          <Typography variant="body1" fontSize={15} color="white" mb={2}>
            {question}
          </Typography>

          {question.includes("Drag") ? (
            <Box
              display="grid"
              gridTemplateColumns="2fr 1fr"
              gap={1}
              sx={{
                bgcolor: "#1A1A24",
                borderRadius: 2,
                p: 1,
                height: 130,
                overflowY: "auto",
                width: 300,
              }}
            >
              {priorityList.map((item, idx) => (
                <React.Fragment key={item}>
                  <Box
                    draggable
                    onDragStart={onDragStart(idx)}
                    onDragOver={onDragOver}
                    onDrop={onDrop(idx)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: "#3C3F51",
                      borderRadius: 2,
                      p: 1,
                      color: "#fff",
                      cursor: "grab",
                      height: 30,
                      width: 150,
                      fontSize: "13px",
                      "&:hover": { bgcolor: "#505364" },
                    }}
                  >
                    {item}
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-start",
                      bgcolor: "#1A1A24",
                      borderRadius: 2,
                      p: 1,
                      color: "#aaa",
                      fontWeight: "bold",
                      fontSize: "12px",
                      height: 30,
                      width: 120,
                    }}
                  >
                    {idx === 0
                      ? "High Priority"
                      : idx === 1
                      ? "Moderate Priority"
                      : "Low Priority"}
                  </Box>
                </React.Fragment>
              ))}
            </Box>
          ) : (
            <>
              <RadioGroup
                value={answer || ""}
                onChange={(e) => handleAnswer(current, e.target.value)}
                sx={{ color: "#fff" }}
              >
                {options.map((opt) => (
                  <FormControlLabel
                    key={opt}
                    value={opt}
                    control={<Radio sx={{ color: "#fff" }} />}
                    label={
                      <Typography color="#fff" fontSize={13}>
                        {opt}
                      </Typography>
                    }
                  />
                ))}
              </RadioGroup>

              {current === 2 && answer === "Yes" && (
                <TextField
                  variant="outlined"
                  placeholder="Enter domain"
                  value={domain}
                  onChange={(e) => setDomain(e.target.value)}
                  sx={{ bgcolor: "#fff", mt: 2 }}
                />
              )}
            </>
          )}
        </Box>
      </Fade>
      <Box
        display="flex"
        justifyContent="flex-start"
        flexWrap={"wrap"}
        width="100%"
        mt={4}
      >
        <Button
          onClick={prev}
          disabled={current === 0}
          sx={{ ...tertiaryButtonStyles, mr: 1 }}
        >
          Previous
        </Button>

        {current !== 2 ? (
          <Button
            sx={primaryButtonStyles}
            onClick={next}
            variant="contained"
            disabled={answer == null}
          >
            {current === questions.length - 1 ? "Submit" : "Next"}
          </Button>
        ) : (
          <Button sx={primaryButtonStyles} onClick={next} variant="contained">
            Create
          </Button>
        )}
      </Box>
    </Box>
  );
}
